/**
 * Form behavior rules — runtime evaluator (Phase 1).
 *
 * Pure function. Given a rule list and a current value map, returns
 * the effective form state. Re-run on every render in the consumer;
 * no internal caching, no side effects.
 *
 * 0.36.96 — Phase 1: complete implementation of the schema defined in
 * `./schema.js` (committed in 0.36.88). Operators: equals, not_equals,
 * in, is_set, is_empty. Effects: show, hide, set_required, clear_value,
 * set_value. Conditions: only `when.all` (AND-joined) for now; `any`
 * (OR-joined) and nested booleans land in a later phase.
 *
 * Semantics:
 *   - Rules evaluate in array order.
 *   - For each rule with `enabled: true`, the `when` condition is
 *     tested against the values map.
 *   - If true, `then` effects apply; if false, `else` effects apply
 *     (when present).
 *   - Effects on the same field LAST-WRITE-WINS — a later rule's
 *     `hide` overrides an earlier rule's `show`, etc.
 *   - Rules see actual stored values, not visibility-filtered ones.
 *     A rule whose condition references a hidden field still sees
 *     that field's value. (This makes evaluation order-independent
 *     of which fields are currently visible.)
 *   - Operators with missing `field` keys silently no-op (rule treated
 *     as false). Operators with unknown `op` also silently no-op.
 *     Phase 2 may add schema validation that surfaces these in the
 *     visual rule editor; Phase 1 is intentionally permissive so
 *     hand-authored JSON doesn't blow up the form.
 *   - All effects with empty `fields` arrays are no-ops.
 *
 * Return shape:
 *   {
 *     hidden:        Set<string>,         // field names hidden right now
 *     required:      Map<string, bool>,   // field name → bool (overrides static)
 *     forcedValues:  Map<string, any>,    // field name → value the rule wants
 *     forcedEmpty:   Set<string>,         // field names the rule wants cleared
 *     rulesFired:    Set<string>,         // rule ids whose `when` was true
 *     byField:       Map<string, [{rule, effect}]>  // diagnostic: which
 *                                                    // rules affect which fields,
 *                                                    // surfaced as admin badges
 *   }
 *
 * The consumer applies this state. In admin preview:
 *   - hidden  → render the element faded with a "hidden by rule" badge
 *   - required → drive the asterisk + validation
 *   - forcedValues / forcedEmpty → drive input value + disable user typing
 *   - byField → render small rule-attribution badges on each affected field
 *
 * In public submission:
 *   - hidden → submit with __hidden: true but skip required check
 *   - forcedValues / forcedEmpty → use those values in the payload
 */

// 0.37.24 — `=`-prefixed set_value strings are evaluated as
// arithmetic expressions against the current values map. See
// ./expression.js for the grammar and semantics.
// 0.37.25 — Expressions now support a `lib()` function that reads
// from any cached library. See ../libraryCache.js.
// 0.44.60 - =removemd(text).
import { splitPhone, layDigits, digitsOf as phoneDigits } from '../phoneCodes';
import { stripMarkdown } from '../markdownPlain';

/* 0.49.74 - Same content: what changed() tests. */
function _bareValue(v) {
  if (v === undefined || v === null || v === '') return null;
  if (Array.isArray(v)) return v.map(_bareValue);
  if (typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v).sort()) { const b = _bareValue(v[k]); if (b !== null) out[k] = b; }
    return Object.keys(out).length ? out : null;
  }
  return typeof v === 'number' ? String(v) : v;
}
function sameContent(a, b) {
  return JSON.stringify(_bareValue(a)) === JSON.stringify(_bareValue(b));
}
// 0.47.73 - Number fields' Min / Max.
import { numberRangeError, hasPattern } from '../numberRange';
// 0.44.70 - =formatdate(value, pattern), =parsedate(text, pattern).
import { formatDatePattern, parseDatePattern, readIso } from '../dateFormat';
import { currentTimezone } from '../../utils/format';
import { readPath } from '../apiResponsePath';
import { looseNumber } from '../looseNumber';
import { upperHy } from '../armenianCaps';
import { readValue } from '../readValue';
import { isExpression, extractExpression, evaluateExpression } from './expression';
import { lookupLibraryValue, getCachedLibrary, fetchLibrary } from '../libraryCache';
// 0.37.54 — Shared option resolver — same code path as the renderer's
// dropdowns use. labels_of() calls this so its labels can NEVER drift
// from what the dropdown displays.
import { resolveElementOptions } from '../resolveElementOptions';
// 0.38.24 — Platform-wide number-format defaults (hydrated at App
// mount from /api/public/settings). concat() and the `&` operator
// call this to apply the cascade to each concatenated fragment
// without the caller having to plumb the value in.
import { getNumberFormatDefaults } from '../../utils/numberFormat';

/* ------------------------------------------------------------------
 * Value helpers — used both for condition operators and to detect
 * "empty" for is_set / is_empty. Centralizing these makes the meaning
 * of "empty" consistent across the evaluator.
 * ------------------------------------------------------------------ */

/**
 * "Empty" is intentionally generous to match how non-developer admins
 * write rules. Treats undefined, null, '', [] and {} as empty. Numbers
 * (including 0) and false are NOT empty — they're real values. This
 * mirrors common form-validation libraries.
 */
function isEmpty(v) {
  if (v === undefined || v === null) return true;
  /* 0.42.47 - A switched-OFF toggle is empty.
   *
   * A Toggle stores true or false. `false` counted as a value, so
   * `is filled` was true whether it was on or off, and no condition
   * could tell the two apart. Off is now empty, on is filled - which is
   * also what "required" should mean for a consent switch. */
  if (v === false) return true;
  if (typeof v === 'string') return v === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

function isSet(v) {
  return !isEmpty(v);
}

/* 0.42.47 - Strict equality, except that a yes/no value also equals its
 * word: true and "true", false and "false" (any letter case, spaces
 * ignored). Nothing else is loosened - "1" still does not equal 1, as
 * before, so no existing rule changes meaning. */
function toNumberOrNull(x) {
  if (x === undefined || x === null || x === '' || typeof x === 'boolean') return null;
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  const s = String(x).trim().replace(/[\s\u00a0\u202f,]/g, '');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function sameValue(a, b) {
  if (a === b) return true;
  if (typeof a === 'boolean' && typeof b === 'string') {
    return String(a) === b.trim().toLowerCase();
  }
  if (typeof b === 'boolean' && typeof a === 'string') {
    return String(b) === a.trim().toLowerCase();
  }
  // 0.47.55 - A library dropdown that loads its options from the
  // server keeps a number column's value as a number (1), while a
  // rule's value is text ("1"). They are the same choice.
  if (typeof a === 'number' && typeof b === 'string') {
    return b.trim() !== '' && Number(b.trim()) === a;
  }
  if (typeof b === 'number' && typeof a === 'string') {
    return a.trim() !== '' && Number(a.trim()) === b;
  }
  return false;
}

// 0.37.42 — Element types that count as user-fillable inputs for
// the all_required_filled operator. Mirrors PageContentRenderer's
// INPUT_TYPES; kept local so evaluate.js doesn't depend on the
// renderer module.
/* 0.42.47 - File upload and Toggle added.
 *
 * This set decides which fields count for "all required fields are
 * filled" and for effects aimed at every required field. Both were
 * missing, so a REQUIRED file upload left empty did not stop "all
 * required filled" from being true - a rule enabling Submit on it let
 * the form through without the document.
 *
 * A file upload is filled once a file is chosen (its value is the file
 * names). A toggle is filled when it is ON - "required" on a consent
 * switch means it must be switched on. */
const INPUT_ELEMENT_TYPES = new Set([
  'text', 'password', 'textarea', 'number', 'email',
  'dropdown', 'multiselect', 'radio', 'checkbox',
  'date', 'phone',
  'file_upload', 'toggle',
  // 0.48.71 - Filled when every required part is (its value is set).
  'cascade',
]);

// 0.37.47 — Sentinel value admin can put in an effect's `fields`
// array to mean "target every currently-required visible input".
// Resolved at evaluation time in applyEffect — the actual runtime
// field list depends on the current required/hidden state, not
// a static snapshot from when the rule was authored. Exported so
// the visual editor can reference the exact same string.
export const ALL_REQUIRED_SENTINEL = '__all_required__';

function _walkInputElements(structure, callback) {
  if (!Array.isArray(structure)) return;
  for (const el of structure) {
    if (!el || typeof el !== 'object') continue;
    if (INPUT_ELEMENT_TYPES.has(el.type)) callback(el);
    // 0.48.63 - A Modal's fields are drafts edited in its window: they
    // never count for the page's "all required fields are filled".
    if (el.type === 'column_group' && el.modal) continue;
    if (Array.isArray(el.children)) _walkInputElements(el.children, callback);
  }
}

/**
 * 0.37.42 — True when every required-and-visible input in the form
 * has a non-empty value. Ignored: inputs that aren't required,
 * and inputs that are hidden. Hidden = element.hidden config OR
 * `hide` effect from an earlier-firing rule (via stateSoFar).
 * Required = element.required config OR set_required effect from
 * an earlier-firing rule (via stateSoFar).
 *
 * Because ruleState is built one rule at a time, `stateSoFar`
 * reflects effects from rules earlier in the array only. If admin
 * wants this operator to see a specific hide / set_required
 * effect, the rule producing that effect must sit ABOVE the rule
 * consuming this operator in the rules[] order.
 *
 * With no structure passed, returns true — legacy callers get a
 * safe no-op (rules can't accidentally block when the runtime
 * hasn't opted in).
 */
function _allRequiredFilled(structure, values, stateSoFar) {
  if (!Array.isArray(structure) || structure.length === 0) return true;
  let allFilled = true;
  _walkInputElements(structure, (el) => {
    if (!allFilled) return;
    const eid = el.element_id || el.name || '';
    if (!eid) return;
    /* 0.47.73 - A visible Number field outside its Min / Max holds no
       valid value, required or not: the form is not complete. */
    if ((el.type === 'number' || hasPattern(el)) && values) {
      const hiddenNow = !!el.hidden
        || (stateSoFar && stateSoFar.hidden && stateSoFar.hidden.has(eid));
      if (!hiddenNow && numberRangeError(el, values[eid], values)) { allFilled = false; return; }
    }
    // Effective required: element.required, overridden by state.required.
    let isRequired = !!el.required;
    if (stateSoFar && stateSoFar.required && stateSoFar.required.has(eid)) {
      isRequired = !!stateSoFar.required.get(eid);
    }
    if (!isRequired) return;
    // Effective hidden: element.hidden config OR ruleState.hidden.
    const isHidden = !!el.hidden
      || (stateSoFar && stateSoFar.hidden && stateSoFar.hidden.has(eid));
    if (isHidden) return;
    const v = values ? values[eid] : undefined;
    if (!isSet(v)) allFilled = false;
  });
  return allFilled;
}

/* ------------------------------------------------------------------
 * Condition evaluation.
 *
 * Conditions are { all: [ {field, op, value?}, ... ] }. Each leaf is
 * tested in order; ANY leaf failing → whole condition false. Missing
 * fields, unknown operators, or empty `all` arrays all return false
 * — a rule with no condition can't fire. (This is the safer default
 * for hand-authored JSON; a user who wants "always fire" can write
 * `is_set` on a known-present field.)
 * ------------------------------------------------------------------ */

let _leafFunctions = null;
function _leafFormula(raw, values) {
  try {
    const out = evaluateExpression(extractExpression(raw), values, _leafFunctions || {});
    return out === undefined || out === null ? '' : out;
  } catch (_e) {
    return '';
  }
}

function evaluateLeaf(leaf, values, changedFields, clickedButtonId, structure, stateSoFar, apiState, pageJustLoaded) {
  if (!leaf) return false;
  // 0.39.82 - "always" (aka "whatever" / any condition) - fires
  // unconditionally, regardless of any field value. Its negation
  // "never" never fires. Handled before the field guard because
  // neither needs a field.
  if (leaf.op === 'always') return true;
  if (leaf.op === 'never') return false;
  // 0.37.42 → 0.37.47 — Form-level operators — either the positive
  // (`all_required_filled`) or its negation. Handled before the
  // `typeof leaf.field !== 'string'` guard below because
  // leaf.field is legitimately empty here.
  if (leaf.op === 'all_required_filled') {
    return _allRequiredFilled(structure, values, stateSoFar);
  }
  if (leaf.op === 'not_all_required_filled') {
    return !_allRequiredFilled(structure, values, stateSoFar);
  }
  if (typeof leaf.field !== 'string') return false;
  // 0.45.89 - A dotted name may read into a flow variable's row.
  /* 0.49.08 - Or the condition is on a formula: `=sum(column(t_checked,
     "c1")) equals 0`. Worked out on every evaluation, so it follows the
     values it reads. A formula that cannot be worked out yet reads as
     empty. */
  const v = isExpression(leaf.field)
    ? _leafFormula(leaf.field, values)
    : readValue(values, leaf.field);
  switch (leaf.op) {
    /* 0.42.47 - Yes/no values compare with the words a rule is typed in.
     * A Toggle stores true/false, but the rule editor's value is TEXT,
     * so `equals "true"` compared true with "true" and never matched.
     * sameValue() lets true equal "true" and false equal "false"; every
     * other comparison is exactly as strict as before. */
    /* 0.47.73 - Number comparisons. The field's value and the rule's
       value are read as numbers (spaces and thousands commas ignored);
       an empty or non-numeric side makes the condition false. */
    case 'less_than':
    case 'more_than':
    case 'at_least':
    case 'at_most': {
      const a = toNumberOrNull(v);
      const b = toNumberOrNull(leaf.value);
      if (a === null || b === null) return false;
      if (leaf.op === 'less_than') return a < b;
      if (leaf.op === 'more_than') return a > b;
      if (leaf.op === 'at_least') return a >= b;
      return a <= b;
    }
    case 'equals':
      return sameValue(v, leaf.value);
    case 'not_equals':
      return !sameValue(v, leaf.value);
    case 'in':
      // `value` should be an array. Tolerate non-array gracefully.
      return Array.isArray(leaf.value) && leaf.value.some(x => sameValue(v, x));
    case 'not_in':
      // 0.36.108 — Negation of `in`. True when the value is NOT in
      // the given list. With a non-array `leaf.value` we tolerate
      // gracefully and treat as "no list to check against" → true
      // (the field's value is trivially "not in" an empty list).
      return !Array.isArray(leaf.value) || !leaf.value.some(x => sameValue(v, x));
    // 0.41.16 - API call state. leaf.field names the CONNECTION.
    /* 0.42.51 - "when the page opens".
     *
     * MOMENTARY, like just_clicked: true only on the first evaluation
     * after the page appears. A condition that stayed true would make
     * `call API` fire again on every evaluation - and each call changes
     * the state, which causes another evaluation, so the page would
     * call the far end forever.
     *
     * This is what lets a page fetch without the citizen doing
     * anything: a rule of "when the page opens -> call API" runs once.
     */
    case 'page_loads':
      return !!pageJustLoaded;
    case 'api_pending':
      return (apiState && apiState[leaf.field] || {}).status === 'pending';
    case 'api_ok':
      return (apiState && apiState[leaf.field] || {}).status === 'ok';
    case 'api_answer_is': {
      // 0.43.67 - Only a successful answer can match; a failed or pending
      // call never shows a hint meant for a real answer.
      const st = (apiState && apiState[leaf.field]) || {};
      if (st.status !== 'ok' || !leaf.path) return false;
      const got = readPath(st.response, leaf.path);
      const v = Array.isArray(got) ? got[0] : got;
      return v !== undefined && v !== null && String(v) === String(leaf.value ?? '');
    }
    /* 0.48.29 - The result of "create account" (renderer: apiState
       under __account). Field-free: leaf.field is "account". */
    case 'account_created': {
      const st = (apiState && apiState.__account) || {};
      return st.status === 'ok' && !st.pending;
    }
    case 'account_pending': {
      const st = (apiState && apiState.__account) || {};
      return st.status === 'ok' && !!st.pending;
    }
    case 'account_running':
      return ((apiState && apiState.__account) || {}).status === 'pending';
    /* 0.48.38 - A code was emailed and the account waits for it. Stays
       true through a wrong code or a resend, until the account is made. */
    case 'account_code_sent': {
      const st = (apiState && apiState.__account) || {};
      return !!st.code && st.status !== 'ok';
    }
    case 'account_failed': {
      const st = (apiState && apiState.__account) || {};
      if (st.status !== 'failed') return false;
      const want = (leaf.value || '').trim();
      return !want || st.failure === want;
    }
    /* 0.48.52 - The result of "update account" (apiState under
       __account_update). Updated means the request succeeded, whether
       or not anything differed. */
    case 'account_updated':
      return ((apiState && apiState.__account_update) || {}).status === 'ok';
    case 'account_update_running':
      return ((apiState && apiState.__account_update) || {}).status === 'pending';
    case 'account_update_code_sent': {
      const st = (apiState && apiState.__account_update) || {};
      return !!st.code && st.status !== 'ok';
    }
    case 'account_update_failed': {
      const st = (apiState && apiState.__account_update) || {};
      if (st.status !== 'failed') return false;
      const want = (leaf.value || '').trim();
      return !want || st.failure === want;
    }
    case 'api_failed': {
      const st = (apiState && apiState[leaf.field]) || {};
      if (st.status !== 'failed') return false;
      // With no class given, any failure matches. With one, only that
      // class - so a form can word "too many requests" differently
      // from "service unavailable".
      const want = (leaf.value || '').trim();
      return !want || st.failure === want;
    }
    case 'is_set':
      return isSet(v);
    case 'is_empty':
      return isEmpty(v);
    // 0.37.28 — is_changed / is_unchanged consult the runtime
    // "value differs from initial" set. The consumer computes
    // changedFields by diffing current values against
    // initialValuesFromStructure(structure); if that set isn't
    // passed (e.g. an older caller of evaluateRules), both
    // operators evaluate as false — safer than pretending
    // everything is unchanged (which could enable rules that
    // depend on the negation, `is_unchanged`, when the caller
    // hasn't opted in).
    case 'is_changed':
      return !!(changedFields && changedFields.has(leaf.field));
    case 'is_unchanged':
      return !!(changedFields && !changedFields.has(leaf.field));
    // 0.37.32 — Edge condition: true only on the render that
    // immediately follows a click on the button whose element_id
    // matches leaf.field. clickedButtonId is set by the consumer
    // (PageContentRenderer / AdminFormPreview) when a button
    // click handler fires, and cleared by a post-render useEffect
    // one tick later — so the rule fires ONCE per click, not
    // continuously while the button is "still" in some clicked
    // state. Rule engines that don't pass clickedButtonId (old
    // callers) see it as undefined and just_clicked evaluates
    // false, which is the safe default.
    case 'just_clicked':
      /* 0.45.98 - One click can name several ids ("card.action|cards":
         a card's button inside a repeating display), and an element
         matches a click on any of its own buttons: "card was just
         clicked" fires for card.action and card.edit alike. */
      if (!clickedButtonId || !leaf.field) return false;
      return String(clickedButtonId).split('|').some(id =>
        id === leaf.field || id.startsWith(`${leaf.field}.`));
    default:
      return false;
  }
}

function evaluateCondition(when, values, changedFields, clickedButtonId, structure, stateSoFar, apiState, pageJustLoaded) {
  if (!when || typeof when !== 'object') return false;
  const all = Array.isArray(when.all) ? when.all : null;
  const any = Array.isArray(when.any) ? when.any : null;

  // 0.36.108 — A rule's `when` now supports BOTH clauses:
  //   - `all`: every leaf must match (AND)        — already supported
  //   - `any`: at least one leaf must match (OR)  — new
  //
  // Semantics when both are present: both clauses must be satisfied
  // (AND between the clauses). Either clause empty/absent is treated
  // as trivially-true (won't constrain the rule). If BOTH are absent
  // or empty, the rule never fires — same conservative default as
  // before. (An admin who wants "always fire" can write a single
  // `is_set` on a known-present field.)
  const allHasLeaves = all && all.length > 0;
  const anyHasLeaves = any && any.length > 0;
  /* 0.47.75 - OR groups: when.groups = [{ all: [leaf, ...] }, ...]. The
     rule's condition then also needs at least one group whose
     conditions all match: (1 and 2) or (1 and 3) or (4 and 5). A group
     with no conditions is skipped. */
  const groups = Array.isArray(when.groups)
    ? when.groups.filter(g => g && Array.isArray(g.all) && g.all.length > 0) : [];
  if (!allHasLeaves && !anyHasLeaves && groups.length === 0) return false;
  if (groups.length > 0) {
    const ev = (leaf) => evaluateLeaf(leaf, values, changedFields, clickedButtonId, structure, stateSoFar, apiState, pageJustLoaded);
    if (!groups.some(g => g.all.every(ev))) return false;
  }

  // All-clause: short-circuit on first failure.
  if (allHasLeaves) {
    for (const leaf of all) {
      if (!evaluateLeaf(leaf, values, changedFields, clickedButtonId, structure, stateSoFar, apiState, pageJustLoaded)) return false;
    }
  }
  // Any-clause: short-circuit on first success.
  if (anyHasLeaves) {
    let matched = false;
    for (const leaf of any) {
      if (evaluateLeaf(leaf, values, changedFields, clickedButtonId, structure, stateSoFar, apiState, pageJustLoaded)) { matched = true; break; }
    }
    if (!matched) return false;
  }
  return true;
}

/* ------------------------------------------------------------------
 * Main entry point.
 * ------------------------------------------------------------------ */

export function evaluateRules(rules, values, lang, changedFields, clickedButtonId, structure, librariesData, apiState, pageJustLoaded = false) {
  const state = {
    // 0.41.16 - Calls this evaluation WANTS made. The engine cannot
    // make them: it runs inside a useMemo and must stay pure, while a
    // call takes seconds. The renderer drains this and feeds results
    // back in as `apiState`.
    apiCalls:     [],
    /* 0.42.85 - Where a flow should go next, if a rule said so: 'next',
       'previous', or null. A form drives its own navigation from its own
       buttons, so the runtime offers no Next of its own. */
    flowMove:     null,
    // 0.43.90 - An Error ID a rule set; the flow shows its error page.
    flowError:    null,
    /* 0.48.29 - "create account" to run: { ruleId, index, effect,
       byClick }. Carried out by the renderer on the server. */
    accountCreate: null,
    // 0.48.52 - "update account" to run, the same shape.
    accountUpdate: null,
    // 0.48.63 - Modals to open / close on this evaluation (Element IDs).
    modalOpen:    [],
    modalClose:   [],
    // 0.48.64 - Values written into a table's clicked row: [{ table,
    // values: [{ column, value }] }], carried out by the renderer.
    rowSets:      [],
    // 0.48.89 - Server copy operations: [{ op: fill | set | reset, name,
    // values: [{ path, value }] }], carried out by the renderer.
    copyOps:      [],
    // 0.48.38 - Confirm the emailed code / send it again.
    accountConfirm: null,
    accountResend: false,
    clicked:      !!clickedButtonId,
    hidden:       new Set(),
    required:     new Map(),
    forcedValues: new Map(),
    forcedEmpty:  new Set(),
    // 0.36.98 — Fields locked by a `lock` effect. Distinct from
    // forcedValues / forcedEmpty: those imply a lock AND impose a
    // value; `lock` locks without changing the value (citizen's
    // input flows through unchanged but the input is disabled).
    locked:       new Set(),
    // 0.36.711 — Element-render overrides applied by rules. These
    // don't touch the form values map — they're consumed by the
    // renderer (PageContentRenderer / AdminFormPreview) to override
    // an element's `label` or `action_value` at render time. When no
    // rule is currently overriding, the element's base value shows.
    labels:       new Map(),
    // 0.37.76 — Companion to labels: display-element text overrides
    // (title, hint, markdown). set_text writes here; renderers for
    // those elements read via state.texts.get(fieldName) and fall
    // back to element.text when no override is set.
    texts:        new Map(),
    // 0.40.00 — Alert variant overrides (info / success / warning /
    // error). set_variant writes here; renderAlert reads via
    // state.variants.get(elementId) and falls back to the element's
    // configured variant when no rule has set one.
    variants:     new Map(),
    // 0.37.85 — Table overlays for the manual-table mutation
    // effects (set_cell, add_row, remove_row). Keyed by the
    // table element's element_id; value is a shape like:
    //   {
    //     cellOverrides: Map<`r${row}c${col}`, value>,  // 1-based
    //     rowsAdded:     Array<Array<string>>,          // appended
    //     rowsRemoved:   Set<number>,                   // 1-based
    //   }
    // Renderer (renderTable in PageContentRenderer.jsx) applies
    // the overlay in order: remove → add → cellOverride, then
    // runs cell interpolation + expression eval over the
    // effective row set. Sticky-on-click via the shared sticky
    // pipeline for the click-triggered case; reactive triggers
    // just read state.tableOverlays each render.
    tableOverlays: new Map(),
    actionValues: new Map(),
    // 0.36.109 — Two sets now: `rulesFired` for rules whose THEN
    // branch is active (when condition matches), `rulesElseFired`
    // for rules whose ELSE branch is active (when doesn't match AND
    // else_when matches if present). Edge-triggered consumers
    // (PageContentRenderer's set_value/clear_value useEffect) diff
    // both sets to detect transitions and apply the right effects.
    rulesFired:     new Set(),
    rulesElseFired: new Set(),
    // 0.36.282 — Dropdown / multiselect rule overrides.
    //   sortOrder    : Map<fieldName, 'asc'|'desc'|'none'> — overrides
    //                  the element's static `sort`. Renderer reads
    //                  this in _resolveElementOptions and applies the
    //                  rule-driven order over the element's static
    //                  setting. Last-write-wins.
    //   addedOptions : Map<fieldName, Array<{value,label}>> — runtime
    //                  options pushed onto the field's list by the
    //                  `add_option` effect. Distinct from the
    //                  element's static `extra_options` so admin can
    //                  tell at a glance whether an option came from
    //                  the persisted JSON or from a rule firing.
    //                  Multiple `add_option` effects on the same
    //                  field accumulate (NOT last-write-wins) so
    //                  several rules can each contribute one option.
    sortOrder:    new Map(),
    addedOptions: new Map(),
    // 0.49.82 - removedOptions : Map<fieldName, Set<string>> - values
    //                  the `remove_option` effect leaves out of the
    //                  field's list. Several effects add up. The value
    //                  the field holds now is never left out (see
    //                  resolveElementOptions), so a Repeater entry
    //                  being edited keeps its own choice.
    removedOptions: new Map(),
    // 0.37.24 — Set of field names whose set_value was an Excel-style
    // '=' expression (see ./expression.js). Consumers use this to
    // decide whether to apply the value ONCE on rule fire (default —
    // stays in forcedValues but consumer edge-triggers it) or
    // CONTINUOUSLY on every render (expression fields — the formula
    // must recompute when any input changes, same as spreadsheets).
    // The evaluated value itself lives in forcedValues alongside
    // non-expression set_values; this Set just marks which entries
    // are formula-sourced.
    expressionFields: new Set(),
    // 0.37.47 — Per-field error state, populated by the
    // set_error_message and show_error_message effects.
    //   errors     : Map<fieldName, string> — the message text a
    //                rule set for the field. Empty string clears.
    //                Renderers read this to display the message
    //                below (or beside) the field. Distinct from
    //                the base validation-error mechanism the
    //                platform will later add for backend errors;
    //                rule-set errors overlay on top.
    //   showError  : Set<fieldName> — fields where an error UI
    //                should be visible. Populated by BOTH effects
    //                (set_error_message auto-adds so admin
    //                doesn't have to fire two effects for the
    //                common case), or by show_error_message
    //                alone (useful for triggering the display of
    //                errors set earlier or by other machinery).
    errors:       new Map(),
    showError:    new Set(),
    // 0.37.48 — Fields whose "changed" baseline the consumer
    // should reset to their current value. Populated by the
    // mark_unchanged effect. Edge-triggered by the consumer
    // (like set_value / clear_value): applied ONCE per rule
    // fire transition, not continuously. Persistent behavior
    // (baseline stays at snapshot until a later mark_unchanged
    // fires or the citizen edits the field to a new value)
    // lives in the consumer's own state, not here.
    markedUnchanged: new Set(),
    byField:      new Map(),
  };

  // 0.38.33 - Seed the hidden set from the structure's initial
  // element.hidden flag. Admin sets this via the "Invisible" checkbox
  // in the form editor when they want an element to START hidden
  // and only appear when a rule's `show` effect fires. Without this
  // seed, element.hidden was only enforced by _isAllRequiredFilled
  // (so a hidden field didn't block required-validation) but the
  // renderer had no way to know - the field was silently rendered
  // visible on load until a rule ran hide on it explicitly.
  //
  // Sequence: seed happens BEFORE the rule loop below, so any
  // rule's `show` effect (state.hidden.delete(name)) can override
  // the seed - and any rule's `hide` on a visible element still
  // works normally (state.hidden.add(name)).
  if (Array.isArray(structure)) {
    const walkForHidden = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (!n || typeof n !== 'object') continue;
        const eid = n.element_id || n.name;
        if (eid && n.hidden === true) state.hidden.add(eid);
        if (Array.isArray(n.children)) walkForHidden(n.children);
        if (Array.isArray(n.columns)) {
          for (const c of n.columns) {
            if (c && Array.isArray(c.children)) walkForHidden(c.children);
          }
        }
      }
    };
    walkForHidden(structure);
  }

  if (!Array.isArray(rules) || rules.length === 0) return state;
  const safeValues = (values && typeof values === 'object') ? values : {};

  // 0.37.25 — Function registry for the expression evaluator. Each
  // entry maps a function name (as admin writes it in an
  // expression, e.g. `lib(...)`) to its JS implementation.
  //
  //   lib(libKey, keyColumn, keyValue, valueColumn)
  //     → the value of `valueColumn` on the row where
  //       `keyColumn` == `keyValue`, from the library `libKey`,
  //       localised to the current UI language when the column
  //       is translatable.
  //     → 0 if the library isn't cached, the key doesn't match,
  //       or the value column is missing (expression.js coerces
  //       undefined function returns to 0 so downstream arithmetic
  //       doesn't NaN-out silently on cache miss).
  //
  // Missing / wrong-typed args return undefined from the underlying
  // lookup (→ 0 in the expression). Arg 3 (keyValue) is stringified
  // both sides inside lookupLibraryValue so a numeric field value
  // matches a string library key without admin thinking about it.
  //
  // Fire-and-forget fetch inside lookupLibraryValue means the first
  // render after a form load returns 0; when the fetch resolves,
  // subscribeLibraryCache listeners re-render, ruleState recomputes
  // and the expression yields the real value.
  // 0.37.38 — Functions registry extended beyond lib(): join(),
  // counta() and labels_of() for multi-value fields. Built via
  // the shared buildExpressionFunctions helper so button-click
  // expressions (in the consumers) get the exact same set.
  const functions = buildExpressionFunctions({
    lang, values: safeValues, structure, librariesData, apiState,
  });
  // 0.49.08 - For conditions on a formula (evaluateLeaf).
  _leafFunctions = functions;

  for (const rule of rules) {
    if (!rule || typeof rule !== 'object') continue;
    if (rule.enabled === false) continue;
    const whenFired = evaluateCondition(rule.when, safeValues, changedFields, clickedButtonId, structure, state, apiState, pageJustLoaded);
    if (whenFired) {
      state.rulesFired.add(rule.id);
      const thenEffects = Array.isArray(rule.then) ? rule.then : [];
      for (const effect of thenEffects) applyEffect(effect, rule, state, safeValues, functions, structure, apiState);
    } else {
      // 0.36.109 — else_when is OPTIONAL conditions on the else
      // branch. When present, the else branch only activates if
      // BOTH (a) the rule's `when` failed AND (b) `else_when`
      // matches. When absent, the else branch activates whenever
      // `when` failed (legacy behavior).
      //
      // else_when has the same shape as when: { all: [...], any: [...] }.
      // Both clauses optional within else_when too.
      const hasElse = Array.isArray(rule.else);
      if (!hasElse) continue;
      let elseActive = true;
      if (rule.else_when && typeof rule.else_when === 'object') {
        const ew = rule.else_when;
        const hasAnyClause = (Array.isArray(ew.all) && ew.all.length > 0)
                          || (Array.isArray(ew.any) && ew.any.length > 0);
        // An else_when block with no leaves at all is treated as
        // "no else conditions" (legacy behavior) — don't make admins
        // delete an empty container.
        if (hasAnyClause) {
          elseActive = evaluateCondition(ew, safeValues, changedFields, clickedButtonId, structure, state, apiState, pageJustLoaded);
        }
      }
      if (!elseActive) continue;
      state.rulesElseFired.add(rule.id);
      for (const effect of rule.else) applyEffect(effect, rule, state, safeValues, functions, structure, apiState);
    }
  }

  return state;
}

function applyEffect(effect, rule, state, values, functions, structure, apiState) {
  if (!effect || typeof effect !== 'object') return;

  // 0.38.88 - show_required_errors is handled as a STICKY edge effect
  // in the consumers (PageContentRenderer / AdminFormPreview), NOT
  // reactively here. If it ran reactively in evaluateRules it would
  // re-fire on every render the rule's `when` is true - and because
  // its trigger is the just_clicked edge, that produced the
  // "errors blink for one render then vanish" bug (0.38.87). As a
  // sticky effect it persists past the edge and clears per-field when
  // the field fills. So the evaluator deliberately ignores it (falls
  // through to the no-op default below).

  // 0.37.47 — Expand the "(all required)" sentinel to the list of
  // currently-required visible input names. Same required/hidden
  // semantics as _allRequiredFilled: element.required (or
  // state.required override) AND NOT hidden. Rule ordering matters
  // — the sentinel resolves against the state built by rules
  // ABOVE the current one.
  // 0.41.16 - call_api is handled BEFORE the field-scoped machinery.
  //
  // Every other effect acts on elements, so applyEffect resolves
  // `effect.fields` first and returns early when none resolve. A call
  // targets no element - its inputs are values, not targets - so it
  // has to be handled here or that guard would silently discard it.
  //
  // This does NOT perform the call: applyEffect runs inside the pure
  // evaluation, and a call takes seconds. It records what is wanted;
  // the renderer dispatches it and feeds the result back as apiState.
  //
  // Inputs resolve HERE, where field values are in scope, so the
  // dispatcher receives plain values. That is also what keeps a
  // connection free of any form's element ids - the binding lives in
  // this rule.
  if (effect.effect === 'call_api') {
    const connection = (effect.connection || '').trim();
    if (!connection) return;
    const inputs = {};
    for (const row of (effect.inputs || [])) {
      const name = (row && row.name || '').trim();
      if (!name) continue;
      if (row.source === 'literal') {
        const raw = row.value || '';
        if (isExpression(raw)) {
          // 0.41.41 - An `=` prefix evaluates here as it does
          // everywhere else in the editor. A control that looks the
          // same and silently does not is worse than one that cannot:
          // the API would receive the formula as TEXT, under the
          // platform's credential, with no error anywhere.
          const evaluated = evaluateExpression(
            extractExpression(raw), values, functions);
          if (evaluated === undefined) {
            // Did not evaluate - unknown token, bad syntax, a lib()
            // row that is not there. The input is left UNSUPPLIED, so
            // the backend drops its key entirely rather than sending
            // an empty string. Sending nothing is honest; sending ""
            // is a value the far end has to interpret.
            continue;
          }
          inputs[name] = evaluated;
        } else {
          inputs[name] = raw;
        }
      } else {
        inputs[name] = readValue(values, row.field) ?? '';
      }
    }
    // 0.48.00 - The fields it reads, so a field whose value has
    // characters it does not allow stops the call (renderer).
    state.apiCalls.push({ connection, inputs,
      // 0.48.84 - Past the connection's cache.
      ...(effect.fresh ? { fresh: true } : {}),
      // 0.48.86 - Kept for the visit (lib/visitApi.js).
      ...(effect.keep_visit ? { keep_visit: true } : {}),
      fields: (effect.inputs || []).filter(r => r && r.source !== 'literal' && r.field).map(r => r.field) });
    return;
  }

  /* 0.42.85 - Move through the flow this form is a step of.
   *
   * Like a call, this is an intention recorded here and carried out by
   * the page, because evaluating rules must stay free of side effects.
   * Momentary by construction: a rule fires it from a click, so it is
   * set on that evaluation only and never becomes a loop. */
  if (effect.effect === 'flow_next' || effect.effect === 'flow_previous') {
    state.flowMove = effect.effect === 'flow_next' ? 'next' : 'previous';
    return;
  }
  /* 0.48.29 - Create an account on the server (services/form_signup.py).
     Only WHICH effect it is travels: the server reads its settings from
     the saved page, so nothing here can be forged into an account. */
  if (effect.effect === 'create_account') {
    const index = Array.isArray(rule.then) ? rule.then.indexOf(effect) : -1;
    if (index >= 0) state.accountCreate = { ruleId: rule.id, index, effect, byClick: state.clicked };
    return;
  }
  /* 0.48.63 - Open or close a Modal (a Column group shown as a modal
     window). An intention the renderer carries out, like a flow move. */
  /* 0.48.64 - Write values into the row whose Edit link was clicked.
     The renderer evaluates each value (=modal_phone) and keeps the
     change; only the intention is recorded here. */
  /* 0.48.89 - A server copy (services/server_copy.py on the server):
     only the intention is recorded; the renderer evaluates the values
     and asks the server, which checks every path. */
  if (effect.effect === 'server_copy_fill' || effect.effect === 'server_copy_set'
      || effect.effect === 'server_copy_reset') {
    const op = effect.effect.slice('server_copy_'.length);
    const names = (Array.isArray(effect.fields) ? effect.fields : []).filter(f => typeof f === 'string' && f);
    const vals = (Array.isArray(effect.values) ? effect.values : []).filter(v => v && String(v.path || '').trim());
    for (const n of names) {
      state.copyOps.push({ op, name: n, values: op === 'set' ? vals : [],
        // 0.48.90 - Always a new answer from the API.
        ...(op === 'fill' && effect.fresh ? { fresh: true } : {}) });
    }
    return;
  }
  if (effect.effect === 'set_row') {
    const tables = (Array.isArray(effect.fields) ? effect.fields : []).filter(f => typeof f === 'string' && f);
    const vals = (Array.isArray(effect.values) ? effect.values : []).filter(v => v && String(v.column || '').trim());
    for (const t of tables) state.rowSets.push({ table: t, values: vals });
    return;
  }
  if (effect.effect === 'open_modal' || effect.effect === 'close_modal') {
    const ids = (Array.isArray(effect.fields) ? effect.fields : []).filter(f => typeof f === 'string' && f);
    if (effect.effect === 'open_modal') state.modalOpen.push(...ids);
    else state.modalClose.push(...ids);
    return;
  }
  /* 0.48.52 - Update the signed-in user's account on the server; only
     which effect travels, as for create account. */
  if (effect.effect === 'update_account') {
    const index = Array.isArray(rule.then) ? rule.then.indexOf(effect) : -1;
    if (index >= 0) state.accountUpdate = { ruleId: rule.id, index, effect, byClick: state.clicked };
    return;
  }
  /* 0.48.38 - The second step of a create account that confirms the
     email: the code from a field, or a new code. Carried out by the
     renderer with the create account that sent the code. */
  if (effect.effect === 'confirm_account_code') {
    state.accountConfirm = { codeField: effect.code_field || '', byClick: state.clicked };
    return;
  }
  if (effect.effect === 'resend_account_code') {
    state.accountResend = true;
    return;
  }
  // 0.43.98 - To step 1 (answers kept), or start the flow again
  // (answers and API results cleared, then step 1).
  if (effect.effect === 'flow_first' || effect.effect === 'flow_restart') {
    state.flowMove = effect.effect === 'flow_first' ? 'first' : 'restart';
    return;
  }
  // 0.43.90 - The flow's error state: an intention the flow carries out
  // (it shows the page set for this Error ID). Set on this evaluation
  // only, like a move.
  if (effect.effect === 'set_flow_error') {
    const id = String(effect.error_id || '').trim();
    if (id) state.flowError = id;
    return;
  }

  const rawFields = Array.isArray(effect.fields) ? effect.fields : [];
  let fields = rawFields;
  if (rawFields.includes(ALL_REQUIRED_SENTINEL)) {
    const expanded = new Set();
    for (const f of rawFields) {
      if (f !== ALL_REQUIRED_SENTINEL && typeof f === 'string' && f !== '') {
        expanded.add(f);
      }
    }
    _walkInputElements(structure || [], (el) => {
      const eid = el.element_id || el.name || '';
      if (!eid) return;
      let isRequired = !!el.required;
      if (state.required.has(eid)) isRequired = !!state.required.get(eid);
      if (!isRequired) return;
      const isHidden = !!el.hidden || state.hidden.has(eid);
      if (isHidden) return;
      expanded.add(eid);
    });
    fields = Array.from(expanded);
  }
  if (fields.length === 0) return;
  // 0.37.24 — Precompute the set_value value once per effect.
  // Expression evaluation (Excel-style '=' prefix) doesn't depend
  // on which field the value is being assigned to — same expression
  // yields same value across every field in `effect.fields`.
  // 0.37.25 — Now also passes the functions registry (with lib()).
  let resolvedSetValue = effect.value;
  let skipSetValue = false;
  if (effect.effect === 'set_value' && isExpression(effect.value)) {
    const evaluated = evaluateExpression(extractExpression(effect.value), values, functions);
    if (evaluated === undefined) {
      // Expression didn't parse (bad syntax, unknown token). Skip
      // the effect entirely so the field keeps its current value
      // instead of getting overwritten with junk.
      skipSetValue = true;
    } else {
      resolvedSetValue = evaluated;
    }
  }
  for (const name of fields) {
    if (typeof name !== 'string' || name === '') continue;
    switch (effect.effect) {
      case 'show':
        // Removing from hidden is the inverse of `hide`. If an earlier
        // rule hid this field and a later rule shows it, the later
        // wins — last-write-wins per the schema contract.
        state.hidden.delete(name);
        recordByField(state, name, rule, 'show');
        break;
      case 'hide':
        state.hidden.add(name);
        recordByField(state, name, rule, 'hide');
        break;
      case 'set_required':
        // effect.required is the override flag. Coerce to boolean
        // (admins might write `true` / `false` strings in raw JSON).
        state.required.set(name, !!effect.required);
        recordByField(state, name, rule, 'set_required');
        break;
      case 'clear_value':
        state.forcedEmpty.add(name);
        // `clear_value` cancels any earlier set_value on this field.
        state.forcedValues.delete(name);
        recordByField(state, name, rule, 'clear_value');
        break;
      // 0.41.25 - A value from the connection's last response.
      //
      // An ARRAY is joined rather than dropped: results[].mark_id is
      // the common case, and a field can only hold text. The separator
      // is configurable because ", " suits a sentence and "\n" suits a
      // textarea.
      //
      // A missing path yields '' rather than "undefined" - the citizen
      // should see an empty field, not a JavaScript word.
      case 'set_from_api': {
        if (skipSetValue) break;
        const resp = ((apiState || {})[effect.connection] || {}).response;
        if (resp === undefined) break;
        const picked = readPath(resp, effect.path || '');
        let text;
        if (picked === null || picked === undefined) text = '';
        else if (Array.isArray(picked)) {
          text = picked
            .map(v => (v === null || v === undefined ? '' : String(v)))
            .join(effect.separator === undefined ? ', ' : effect.separator);
        } else text = String(picked);
        state.forcedValues.set(name, text);
        break;
      }
      case 'set_value':
        // 0.37.24 — resolvedSetValue is either the literal
        // effect.value (backward compat: string/number/boolean) or
        // the result of evaluating an Excel-style '=' expression
        // against the current values map. When the expression fails
        // to parse, skipSetValue is true and this effect is a no-op
        // for every field (rather than overwriting them with garbage).
        //
        // For expression values, ALSO mark the field in
        // state.expressionFields so consumers know to re-apply on
        // every render (formula recomputes when inputs change,
        // spreadsheet-style) rather than the default edge-triggered
        // fill-once behavior.
        if (skipSetValue) break;
        state.forcedValues.set(name, resolvedSetValue);
        state.forcedEmpty.delete(name);
        if (isExpression(effect.value)) {
          state.expressionFields.add(name);
        } else {
          // Not from an expression: if a previous rule made it one,
          // this later literal set_value takes over the field back
          // into edge-triggered semantics (last-write-wins).
          state.expressionFields.delete(name);
        }
        recordByField(state, name, rule, 'set_value');
        break;
      case 'lock':
        // 0.36.98 — Lock the field for editing without imposing a
        // value. The citizen sees their existing value (or the
        // form default) but can't change it. Compose with `hide` or
        // `set_value` if needed — last-write-wins per the schema.
        state.locked.add(name);
        recordByField(state, name, rule, 'lock');
        break;
      case 'set_label':
        // 0.36.711 — Override the element's label at render time.
        // Value comes from effect.label; last-write-wins across
        // rules. Renderer reads via state.labels.get(fieldName)
        // and falls back to element.label when no override.
        state.labels.set(name, String(effect.label ?? ''));
        recordByField(state, name, rule, 'set_label');
        break;
      case 'set_text':
        // 0.37.76 — Override the element's `text` at render time.
        // Companion to set_label; used for title / hint / markdown
        // elements whose displayed content is `text`, not `label`.
        // Value comes from effect.text — accepts `=...` expressions
        // the same way set_value does (evaluator hands the raw
        // string here; renderers evaluate expressions when they
        // read state.texts). Last-write-wins across rules.
        state.texts.set(name, String(effect.text ?? ''));
        recordByField(state, name, rule, 'set_text');
        break;
      case 'set_variant':
        // 0.40.00 — Switch an Alert element's variant (info /
        // success / warning / error) at render time, so one alert
        // can change state as the form is filled instead of needing
        // four alerts with show/hide rules on each. Value comes from
        // effect.variant; last-write-wins. The renderer validates the
        // value and falls back to 'info' for anything unrecognised,
        // so a typo degrades to a readable alert rather than an
        // unstyled box.
        state.variants.set(name, String(effect.variant ?? ''));
        recordByField(state, name, rule, 'set_variant');
        break;
      case 'set_cell': {
        // 0.37.85 — Cell override. `name` is the table's
        // element_id; row/column are 1-based.
        // 0.37.88 — row=0 addresses the HEADER row. Convention:
        //   row=0     → header cells (mapped to e.headers[col-1])
        //   row=1..N  → data cells (mapped to e.rows[row-1][col-1])
        // Column stays 1-based throughout. Existing rules with
        // row=1 are unaffected — they still target the first
        // data row. Lazily create the overlay entry for this
        // table if this is the first op to target it.
        const rowN = Number(effect.row);
        const colN = Number(effect.column);
        if (!Number.isFinite(rowN) || rowN < 0
          || !Number.isFinite(colN) || colN < 1) break;
        const overlay = ensureTableOverlay(state, name);
        overlay.cellOverrides.set(`r${rowN}c${colN}`, String(effect.value ?? ''));
        recordByField(state, name, rule, 'set_cell');
        break;
      }
      case 'add_row': {
        // 0.37.85 — Append a row to the table's effective row
        // list. Values shape: array aligned to columns; short
        // arrays render blank cells in the missing columns.
        // Single-string values are wrapped so admin can write
        // `add_row values="hello"` in the DSL and it becomes
        // ["hello"] (single-column tables), instead of "hello"
        // being iterated as characters.
        const values = Array.isArray(effect.values)
          ? effect.values.map(v => String(v ?? ''))
          : (effect.values == null ? [] : [String(effect.values)]);
        const overlay = ensureTableOverlay(state, name);
        overlay.rowsAdded.push(values);
        recordByField(state, name, rule, 'add_row');
        break;
      }
      case 'remove_row': {
        // 0.37.85 — Mark a 1-based row index for removal from
        // the effective row set. Renderer treats indices past
        // the actual row count as no-ops.
        const rowN = Number(effect.row);
        if (!Number.isFinite(rowN) || rowN < 1) break;
        const overlay = ensureTableOverlay(state, name);
        overlay.rowsRemoved.add(rowN);
        recordByField(state, name, rule, 'remove_row');
        break;
      }
      case 'set_action_value':
        // 0.36.711 — Override a button's action_value at render
        // time. Used to swap select_all ↔ clear_all on the same
        // button based on table state. Renderer reads via
        // state.actionValues.get(buttonName) and falls back to the
        // button's base action_value.
        state.actionValues.set(name, String(effect.value ?? ''));
        recordByField(state, name, rule, 'set_action_value');
        break;
      case 'set_sort_order': {
        // 0.36.282 — Override the element's static `sort`. Valid
        // values: 'asc', 'desc', 'none'. Anything else is treated
        // as 'none' (defensive — admin-written JSON shouldn't crash
        // the renderer). Last-write-wins across rules.
        const requested = effect.order;
        const order = (requested === 'asc' || requested === 'desc' || requested === 'none')
          ? requested : 'none';
        state.sortOrder.set(name, order);
        recordByField(state, name, rule, 'set_sort_order');
        break;
      }
      case 'remove_option': {
        // 0.49.82 - A list (a formula such as =column(boxes, "box"))
        // or comma-separated text. Reactive: it holds while the rule's
        // condition is true, so a value comes back when the condition
        // stops or the formula no longer lists it.
        let raw = effect.value;
        if (isExpression(raw)) {
          raw = evaluateExpression(extractExpression(raw), values, functions);
          if (raw === undefined) break;
        }
        const flat = [];
        const walk = (v) => {
          if (v === null || v === undefined || v === '') return;
          if (Array.isArray(v)) { v.forEach(walk); return; }
          if (typeof v === 'object') return;
          String(v).split(',').map(x => x.trim()).filter(Boolean).forEach(x => flat.push(x));
        };
        walk(raw);
        const set = state.removedOptions.get(name) || new Set();
        flat.forEach(v => set.add(v));
        state.removedOptions.set(name, set);
        recordByField(state, name, rule, 'remove_option');
        break;
      }
      case 'add_option': {
        // 0.36.282 — Push a runtime-added option onto the field's
        // option list. Accumulates across rules (not last-write-
        // wins) so multiple rules can each contribute. Skips
        // entries missing value or label since those would render
        // as ghost options.
        const value = effect.value;
        const label = effect.label;
        if (value === undefined || value === null || value === '') break;
        if (label === undefined || label === null || label === '') break;
        const list = state.addedOptions.get(name) || [];
        list.push({ value: String(value), label: String(label) });
        state.addedOptions.set(name, list);
        recordByField(state, name, rule, 'add_option');
        break;
      }
      case 'set_error_message': {
        // 0.37.47 — Set the field's error-message text. Empty
        // string clears the message. Auto-flips the field's
        // showError flag so the message is visible without admin
        // having to fire a second show_error_message effect for
        // the common case. If admin wants to prepare messages
        // silently and show them later, they can use two rules —
        // set_error_message first (leaves showError off if the
        // message is empty), show_error_message when triggered.
        const msg = effect.message === undefined || effect.message === null
          ? ''
          : String(effect.message);
        if (msg === '') {
          state.errors.delete(name);
          state.showError.delete(name);
        } else {
          state.errors.set(name, msg);
          state.showError.add(name);
        }
        recordByField(state, name, rule, 'set_error_message');
        break;
      }
      case 'show_error_message': {
        // 0.37.47 — Force the field's error UI to display. Composes
        // with set_error_message set by an earlier rule, or with
        // any base validation error the platform may attach later.
        // No message text of its own — just flips the display flag.
        state.showError.add(name);
        recordByField(state, name, rule, 'show_error_message');
        break;
      }
      case 'hide_error_message': {
        // 0.37.47 — Reverse of show_error_message: clears the
        // display flag without touching the message text. Admin
        // can use this to hide an error triggered by an earlier
        // rule without wiping the text (which set_error_message
        // to '' would do). The message stays in state.errors for
        // a later show_error_message to re-surface.
        state.showError.delete(name);
        recordByField(state, name, rule, 'hide_error_message');
        break;
      }
      case 'mark_unchanged': {
        // 0.37.48 — Record that this field's "changed" baseline
        // should be reset to its current value. The evaluator
        // just records intent — the actual baseline update
        // happens in the consumer's edge-trigger useEffect
        // (mirrors how set_value / clear_value work — the
        // evaluator produces forcedValues, the consumer applies
        // them). Recording the fields into state.markedUnchanged
        // lets the consumer look at the merged ruleState instead
        // of scanning rule.then / rule.else themselves.
        state.markedUnchanged.add(name);
        recordByField(state, name, rule, 'mark_unchanged');
        break;
      }
      default:
        // Unknown effect — silent no-op. Phase 2's visual editor
        // won't let admins produce these; raw-JSON authors get a
        // permissive fallback rather than a runtime error.
        break;
    }
  }
}

function recordByField(state, fieldName, rule, effectName) {
  if (!state.byField.has(fieldName)) state.byField.set(fieldName, []);
  state.byField.get(fieldName).push({
    ruleId:   rule.id || '(unnamed)',
    ruleName: rule.name || '',
    effect:   effectName,
  });
}

/**
 * 0.37.85 — Table-overlay lazy-init helper. Table-mutation
 * effects (set_cell / add_row / remove_row) share one overlay
 * per table element_id in state.tableOverlays; this helper
 * seeds the entry on first touch so each case handler stays
 * short. The overlay shape is documented next to the
 * state.tableOverlays init in evaluateRules.
 */
function ensureTableOverlay(state, tableName) {
  if (!state.tableOverlays.has(tableName)) {
    state.tableOverlays.set(tableName, {
      cellOverrides: new Map(),
      rowsAdded:     [],
      rowsRemoved:   new Set(),
    });
  }
  return state.tableOverlays.get(tableName);
}

/* ------------------------------------------------------------------
 * Convenience helpers for consumers (renderers).
 * ------------------------------------------------------------------ */

/**
 * Resolves the effective value of a field given rule state and a base
 * value (typically the user-entered value from React state).
 *
 * 0.36.99 — Behavior changed from 0.36.96. set_value / clear_value
 * effects are now EDGE-TRIGGERED: the renderer applies the rule's
 * value to the form's state on rule transition (false→true) and
 * leaves it alone afterward. The user can edit the field freely
 * after the auto-fill. As a consequence, by the time anything
 * calls effectiveValue, baseValue already reflects either the
 * user's edit or the rule's value from the last transition.
 *
 * The function is kept as a passthrough so existing callers don't
 * break. If a future ship reintroduces forced-value-while-active
 * semantics, this is where it would re-enter.
 */
export function effectiveValue(fieldName, baseValue, ruleState) {
  return baseValue;
}

/**
 * True when the field's input should be DISABLED.
 *
 * 0.36.99 — Only the explicit `lock` effect locks now. set_value
 * and clear_value used to imply a lock (the field couldn't be
 * edited while those rules were active), but per user request:
 * "set value should set initial value but allow editing — when I
 * want both, I'll use lock for editing". Those effects became
 * edge-triggered fill-once in 0.36.99, and locking is now the
 * sole job of the `lock` effect.
 */
export function isFieldLocked(fieldName, ruleState) {
  return ruleState.locked.has(fieldName);
}

/**
 * Resolves the effective required flag given the static element's
 * `required` and any rule override. The rule wins when present;
 * otherwise falls back to the static flag.
 */
export function effectiveRequired(fieldName, staticRequired, ruleState) {
  if (ruleState.required.has(fieldName)) return ruleState.required.get(fieldName);
  return !!staticRequired;
}

/**
 * 0.37.38 — Build the functions registry used by the expression
 * evaluator. Called from both evaluateRules (rule-level
 * expressions) and consumer button-click handlers (button
 * `Value to write` expressions), so both surfaces support the
 * same functions.
 *
 * Functions:
 *   lib(libKey, keyColumn, keyValue, valueColumn)
 *     — see 0.37.25 rationale in this file.
 *
 *   textjoin(field, separator)
 *     — Concatenate an array field's items into a string. If
 *       `field` isn't an array (e.g. a scalar dropdown value),
 *       coerces to string and returns it as-is. Default
 *       separator is "," (no space). Empty entries in the
 *       array are skipped (matches Excel TEXTJOIN with
 *       ignore_empty=TRUE).
 *       0.37.78 — Was named `join`; renamed to match Excel's
 *       TEXTJOIN. Existing forms using `join(...)` will fail
 *       with "unknown function" and need to be updated to
 *       `textjoin(...)`.
 *
 *   counta(field)
 *     — Count of items. For arrays, array.length. For scalars,
 *       1 if non-empty (not '', null, undefined) else 0. Matches
 *       Excel's COUNTA behavior — counts non-empty entries
 *       regardless of type. Naming borrowed directly from Excel
 *       to match admin's spreadsheet mental model.
 *
 *   labels_of(fieldName)
 *     — Map an element's currently-selected VALUES to their
 *       LABELS via that element's options list. Takes the
 *       element_id as a STRING LITERAL (not an identifier) so
 *       the function can look up both the current value and the
 *       element's options. For a multiselect holding
 *       ["renewal_each","collective_mark"] with matching
 *       options, returns ["Renewal each","Collective mark"].
 *       For a scalar dropdown, returns the scalar label. If the
 *       element or its options can't be found, returns the
 *       original value unchanged. Requires `structure` in the
 *       builder context — passed by evaluateRules and by
 *       button handlers.
 *
 * Composition example admin can write:
 *
 *   set_value on summary_field = =textjoin(labels_of("mark_types"), ", ")
 *
 * Multiselect `mark_types` holds ["a","b"]. labels_of returns
 * ["A label","B label"]. textjoin returns "A label, B label".
 * summary_field displays that string.
 */
// 0.38.21 - Marker character used by year() and str() to opt out
// of the auto-format-numeric-string behavior in resolveAdminText.
// U+2060 (Word Joiner) is invisible everywhere, has no line-break
// effect, and won't collide with normal text. Exported so the
// renderer's auto-detect can check for its presence and the final
// display code can strip it before showing to citizens.
export const EXPLICIT_STRING_MARKER = '\u2060';


/* 0.43.68 - randbetween(): a random positive whole number.
 *
 * Expressions are recalculated whenever the form changes, so a plain
 * random call would give a new number on every keystroke. Instead each
 * randbetween() in an expression pass draws its number ONCE per page
 * load and keeps it: the Nth call in a pass always returns the Nth
 * number drawn. A reload draws new ones. Uses the browser's
 * cryptographic generator. */
const RAND_SLOTS = new Map();
function randomInt(lo, hi) {
  const span = hi - lo + 1;
  let r;
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    r = buf[0] / 4294967296;
  } else {
    r = Math.random();
  }
  return lo + Math.floor(r * span);
}

export function buildExpressionFunctions({ lang, values, structure, librariesData, formNumberDefaults, apiState } = {}) {
  let randCall = 0;
  return {
    // randbetween()          -> 1 to 1,000,000
    // randbetween(min, max)  -> min to max, both included (as Excel's
    //                           RANDBETWEEN); min at least 1.
    randbetween: (min, max) => {
      let lo = 1;
      let hi = 1000000;
      if (min !== undefined && max !== undefined) {
        lo = Math.max(1, Math.ceil(Number(min)));
        hi = Math.floor(Number(max));
        if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) return undefined;
      }
      const slot = `${randCall++}:${lo}:${hi}`;
      if (!RAND_SLOTS.has(slot)) RAND_SLOTS.set(slot, randomInt(lo, hi));
      return RAND_SLOTS.get(slot);
    },
    /* 0.49.68 - A list as the key (an API array, a multi-select, or
       split("a,b,c", ",")): each item looked up, a list back, in the
       same order, so textjoin(lib(..., list, ...), ", ") gives the
       names. Still loading while any item is. An item the library does
       not have gives "" (textjoin leaves it out). */
    lib: (libKey, keyColumn, keyValue, valueColumn) => {
      if (Array.isArray(keyValue)) {
        const out = keyValue.map(k => lookupLibraryValue(libKey, keyColumn,
          typeof k === 'string' ? k.trim() : k, valueColumn, lang));
        return out.some(v => v === undefined) ? undefined : out;
      }
      return lookupLibraryValue(libKey, keyColumn, keyValue, valueColumn, lang);
    },
    /* 0.49.80 - json(value): a value (a list, a Repeater's entries, an
       API answer) as JSON text, indented: what the page holds, to look
       at or copy. parsejson(text): the reverse, for a starting value
       written as JSON: =parsejson('[{"box": "big"}]'). Text that is not
       JSON gives "". */
    json: (value) => {
      if (value === undefined) return undefined;
      try { return JSON.stringify(value, null, 2); } catch (_e) { return ''; }
    },
    parsejson: (text) => {
      if (text && typeof text === 'object') return text;
      try { return JSON.parse(String(text == null ? '' : text)); } catch (_e) { return ''; }
    },
    /* 0.49.78 - niceclass(basic_number): the Nice class of a term's
       basic number, the digits before its last four: 100048 is 10,
       50082 (or 050082) is 5, 440113 is 44. A list gives a list. */
    niceclass: (v) => {
      const one = (x) => {
        const d = String(x == null ? '' : x).replace(/\D/g, '');
        if (d.length < 5) return '';
        const n = Number(d.slice(0, -4));
        return n >= 1 && n <= 45 ? String(n) : '';
      };
      return Array.isArray(v) ? v.map(one) : one(v);
    },
    /* 0.49.78 - groupjoin(groups, items, itemSep, groupSep, pattern,
       order): the items joined group by group. groups and items are
       two lists of the same length (one group per item, as an API
       gives them: result.products[].acdm_class and
       result.products[].product_key). Each group's items are joined
       with itemSep (", "), each group is written as pattern ("{group}:
       {items}") and the groups are joined with groupSep ("; ", or
       newline()). Groups keep the order they first appear in, or
       order "sort" puts them in order (numbers as numbers). Empty
       items are left out. */
    groupjoin: (groups, items, itemSep, groupSep, pattern, order) => {
      const gs = Array.isArray(groups) ? groups : [groups];
      const is = Array.isArray(items) ? items : [items];
      const map = new Map();
      for (let i = 0; i < is.length; i += 1) {
        const it = is[i];
        if (it === undefined || it === null || it === '') continue;
        const g = gs.length === 1 ? gs[0] : gs[i];
        const key = g === undefined || g === null ? '' : String(g);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(String(it));
      }
      let keys = [...map.keys()];
      if (String(order || '').toLowerCase() === 'sort') {
        const numeric = keys.every(k => /^-?\d+(\.\d+)?$/.test(k));
        keys = keys.sort((a, b) => (numeric ? Number(a) - Number(b) : a.localeCompare(b)));
      }
      const iSep = itemSep === undefined || itemSep === null ? ', ' : String(itemSep);
      const gSep = groupSep === undefined || groupSep === null ? '; ' : String(groupSep);
      const pat = pattern === undefined || pattern === null || pattern === '' ? '{group}: {items}' : String(pattern);
      return keys.map(k => pat.split('{group}').join(k).split('{items}').join(map.get(k).join(iSep))).join(gSep);
    },
    /* 0.49.74 - changed(a, b): 1 when two values differ, 0 when they are
       the same, objects and lists compared by their content and empty
       values ("", null, nothing) all the same. For a server copy:
       changed(adv.result.email, adv_original.result.email). */
    changed: (a, b) => (sameContent(a, b) ? 0 : 1),
    /* 0.49.71 - Phones. The phone picker keeps one value, "+37491010364";
       a register often keeps two, the country code and the number.
         phone(code, number)          "+" and the digits of both:
                                      phone("374", "91-010-364") is
                                      +37491010364, for the picker
         phonecode(phone)             the country code: 374
         phonelocal(phone, pattern)   the number without it, as digits
                                      or laid into a pattern (# for a
                                      digit): "##-###-###" gives
                                      91-010-364
       phonecode reads the country codes of the page's phone picker, so
       it works on a page with a Phone field. */
    phone: (code, number) => {
      const n = phoneDigits(number);
      if (!n) return '';
      return `+${phoneDigits(code)}${n}`;
    },
    phonecode: (value) => splitPhone(value).code,
    phonelocal: (value, pattern) => layDigits(splitPhone(value).local, pattern),
    /* 0.49.68 - split(text, separator): a text cut into a list at each
       separator, every piece trimmed, empty pieces left out:
       split("administrative,civil,criminal", ",") is the list of three.
       A list is kept as it is. No separator: a comma. */
    split: (text, separator) => {
      if (Array.isArray(text)) return text;
      if (text === undefined || text === null) return [];
      const sep = separator === undefined || separator === null || separator === '' ? ',' : String(separator);
      return String(text).split(sep).map(x => x.trim()).filter(Boolean);
    },

    /* 0.41.34 - Read a value from an API connection's last response.
     *
     *     =api("tm_search", "total_matches")
     *     =concat("Found ", api("tm_search", "total_matches"), " marks")
     *
     * A FUNCTION, not dotted access like `tm_search.took_ms`. The
     * expression language reads a bare identifier as a FIELD name, and
     * a form may contain an element called tm_search while a connection
     * is also called that - so dotted access would be ambiguous exactly
     * where the two namespaces collide. Naming the source, as lib()
     * does, never is.
     *
     * This can only READ. Making a call is a rule effect, deliberately:
     * an expression re-evaluates on every keystroke, and a call that
     * costs seconds and third-party quota must never fire that way.
     *
     * Returns undefined before a call has run - the same contract as
     * lib() for a missing row - so surrounding arithmetic yields
     * nothing rather than junk, and a label does not read
     * "Found undefined marks" during the wait.
     *
     * An array (results[].mark_id) is returned as an array, so
     * textjoin() can format it. set_from_api joins for you because a
     * field holds text; an expression can do better.
     */
    api: (connection, path) => {
      const entry = (apiState || {})[String(connection || '').trim()];
      if (!entry || entry.response === undefined) return undefined;
      const picked = readPath(entry.response, String(path || ''));
      return picked === null ? undefined : picked;
    },

    // 0.37.78 — Was `join`. Renamed to match Excel's TEXTJOIN
    // (per admin request — Excel-style function names, same
    // reason `counta` matches Excel's COUNTA). Existing forms
    // using `join(...)` will fail with "unknown function" at
    // evaluation time; they need to be updated to
    // `textjoin(...)`. Arg order kept as-is (field first,
    // separator second) — Excel's TEXTJOIN uses (separator,
    // ignore_empty, ...values) so this is not a full match,
    // but the swap would be a second break admin didn't ask
    // for. If admin wants full Excel alignment later, do the
    // arg swap in a follow-up ship.
    textjoin: (field, separator) => {
      const sep = separator === undefined || separator === null
        ? ','
        : String(separator);
      if (Array.isArray(field)) {
        // Skip null/undefined/empty entries so a partial array
        // doesn't produce "a,,c" with a hole in the middle.
        // Matches Excel TEXTJOIN's default behavior when
        // ignore_empty=TRUE (the common case).
        return field
          .filter(v => v !== null && v !== undefined && v !== '')
          .map(v => String(v))
          .join(sep);
      }
      if (field === null || field === undefined) return '';
      return String(field);
    },

    /* 0.42.45 - index(list, n): the n-th item of a list, as Excel's
     * INDEX does for one row or column.
     *
     *   index(file_upload_1_base64_list, 3)    the third file
     *   index(file_upload_1_size_list, -1)     the LAST file's size
     *
     * Counts from 1, like Excel. A negative n counts from the end, which
     * suits a list built up one choice at a time.
     *
     * A single value is a list of one, as counta() and the API path
     * reader both treat it - so index(x, 1) is x. A comma-joined string
     * is NOT split: a file name may itself contain a comma, and guessing
     * would pick the wrong item. Use the _list fields.
     *
     * Out of range, or n of 0, gives EMPTY rather than an error: an
     * Image asked for a third file when two were chosen shows nothing
     * instead of a broken picture. */
    index: (list, n) => {
      const arr = Array.isArray(list)
        ? list
        : (list === null || list === undefined || list === '' ? [] : [list]);
      const k = Math.trunc(Number(n));
      if (!Number.isFinite(k) || k === 0) return '';
      const i = k > 0 ? k - 1 : arr.length + k;
      if (i < 0 || i >= arr.length) return '';
      const v = arr[i];
      return v === null || v === undefined ? '' : v;
    },

    counta: (field) => {
      if (Array.isArray(field)) {
        // Count non-empty entries only.
        let n = 0;
        for (const v of field) {
          if (v !== null && v !== undefined && v !== '') n++;
        }
        return n;
      }
      if (field === null || field === undefined || field === '') return 0;
      return 1;
    },

    // 0.40.01 - options_count(field) -> how many options the given
    // dropdown / multiselect / radio actually OFFERS.
    //
    // Distinct from the neighbours, which are about the SELECTION:
    //   labels_of(f)  -> label(s) of what is currently selected
    //   counta(f)     -> how many entries the value holds
    //   options_count(f) -> size of the list the user picks FROM
    //
    // Written for text like "The classification includes N classes",
    // where N must follow the library rather than be typed by hand.
    // Uses the SAME resolver the renderer uses, so the number counts
    // exactly what the field displays: library rows, cascade filters,
    // exclude_values and extra_options all already applied. Returns 0
    // when the field is unknown or its library has not loaded yet.
    options_count: (fieldName) => {
      if (fieldName === null || fieldName === undefined) return 0;
      if (typeof fieldName !== 'string' || fieldName === '') return 0;
      if (!structure) return 0;
      const el = _findElByEid(structure, fieldName);
      if (!el) return 0;
      // 0.40.04 - Same cache warmup labels_of does. Without it a
      // library-backed dropdown counted 0 and stayed there: on first
      // evaluation librariesData often has not loaded the library
      // yet, and with no fetch kicked off nothing ever re-evaluated.
      // Falling back to the shared cache (any-language, to survive a
      // language-switch transient) and otherwise starting a fetch
      // means subscribeLibraryCache bumps libTick when it lands and
      // the count re-computes with real rows.
      let effectiveLibraries = librariesData || {};
      if (el.library && !effectiveLibraries[el.library]) {
        const cached = getCachedLibrary(el.library, lang)
          || getCachedLibrary(el.library, null)
          || getCachedLibrary(el.library, 'en')
          || getCachedLibrary(el.library, 'hy');
        if (cached) {
          effectiveLibraries = { ...effectiveLibraries, [el.library]: cached };
        } else {
          fetchLibrary(el.library, lang).catch(() => { /* subscribers re-eval */ });
        }
      }
      // Same source normalisation labels_of does: elements with
      // el.library but no el.source would otherwise fall back to the
      // stale manual stubs and undercount.
      const elForResolver = el.library && !el.source
        ? { ...el, source: 'library' }
        : el;
      const options = resolveElementOptions(elForResolver, {
        librariesData: effectiveLibraries,
        values,
        ruleState: null,
      });
      return Array.isArray(options) ? options.length : 0;
    },

    labels_of: (fieldName) => {
      // 0.37.40 — Accept both forms:
      //   labels_of("multiselect_1")   — string literal
      //   labels_of(multiselect_1)     — bare identifier
      //
      // The expression parser routes bare identifiers through a
      // dedicated code path (see parseFactor's id branch — when
      // the identifier appears as a labels_of() arg, the parser
      // passes it as the raw name STRING rather than resolving it
      // to its value first). So this function always sees a
      // string.
      if (fieldName === null || fieldName === undefined) return '';
      if (typeof fieldName !== 'string') return fieldName;
      if (fieldName === '') return '';
      if (!structure || !values) return values ? values[fieldName] : '';
      const el = _findElByEid(structure, fieldName);
      if (!el) return readValue(values, fieldName);
      const val = values[fieldName];

      // 0.37.54 — Use the SAME resolver the renderer uses. Before
      // this ship, labels_of was re-implementing option resolution
      // and drifted repeatedly (missed cascade filter, wrong
      // priority when el.library AND el.options both present,
      // missed exclude_values, missed extra_options). Every
      // divergence became a separate bug. This ship shares the
      // resolver so labels_of returns EXACTLY the options the
      // dropdown displays — no more drift possible.
      //
      // The resolver is pure and takes ctx.librariesData +
      // ctx.values. Rule overlays (add_option, sort effects) are
      // omitted here because labels_of runs INSIDE ruleState
      // computation — the state isn't fully built yet. For
      // label-lookup purposes that's fine: values selected via a
      // rule-added option will still be in the library / element
      // options (rule adds an option; user picks it; value stored;
      // labels_of resolves it from base options). Only the case
      // where a rule adds an option whose value isn't in base
      // options wouldn't resolve — an edge case, and admin will
      // see the raw value which is a clear signal to check the
      // rule.
      //
      // Cache warmup: if the element is library-backed and
      // librariesData doesn't have it yet, we try the shared
      // library cache (any-lang fallback for language-switch
      // transient) and, on total miss, kick off a fetch. Once
      // the fetch resolves, subscribeLibraryCache bumps libTick,
      // ruleState re-computes, labels_of runs again with the
      // populated cache and returns real labels.
      let effectiveLibraries = librariesData || {};
      if (el.library && !effectiveLibraries[el.library]) {
        const cached = getCachedLibrary(el.library, lang)
          || getCachedLibrary(el.library, null)
          || getCachedLibrary(el.library, 'en')
          || getCachedLibrary(el.library, 'hy');
        if (cached) {
          effectiveLibraries = { ...effectiveLibraries, [el.library]: cached };
        } else {
          fetchLibrary(el.library, lang).catch(() => { /* subscribers re-eval */ });
        }
      }

      // 0.37.55 — Normalize el.source for the resolver. Real-world
      // forms have elements with `el.library` set but `el.source`
      // undefined — leftovers from mode-switching in the editor,
      // imports, older schemas. resolveElementOptions defaults an
      // absent source to 'manual' and uses the stale `el.options`
      // stubs, so labels_of returned raw IDs.
      //
      // Here we infer source='library' whenever el.library is set,
      // so the resolver walks the library rows and returns proper
      // labels. This is a LOCAL override for the labels_of call —
      // the renderer's resolver behavior stays strict (source
      // required to be 'library' explicitly), so we don't
      // accidentally change dropdown rendering for other admins.
      // Once cache warms up, resolveElementOptions has the data
      // it needs and returns labeled options.
      const elForResolver = el.library && !el.source
        ? { ...el, source: 'library' }
        : el;

      const options = resolveElementOptions(elForResolver, {
        librariesData: effectiveLibraries,
        values,
        ruleState: null,
      });

      // Build the value→label map from the resolved options. If
      // resolveElementOptions returns empty (library not cached
      // yet AND no manual options), return the raw value —
      // subscribeLibraryCache will re-fire when the fetch
      // completes.
      if (!Array.isArray(options) || options.length === 0) return val;
      const map = new Map();
      for (const o of options) {
        if (o && typeof o === 'object' && o.value !== undefined) {
          map.set(String(o.value), o.label != null ? String(o.label) : String(o.value));
        }
      }
      const lookup = (v) => {
        const key = String(v);
        return map.has(key) ? map.get(key) : v;
      };
      if (Array.isArray(val)) return val.map(lookup);
      if (val === null || val === undefined || val === '') return val;
      return lookup(val);
    },

    // 0.38.13 - Numeric coercion for arithmetic contexts.
    //
    //   num(x)          → convert x to a Number; falls back to 0 if
    //                     x is undefined, null, empty string, or
    //                     otherwise not parseable
    //   num(x, default) → same, but return the given default instead
    //                     of 0 when x can't be coerced
    //
    // Rationale: since 0.38.02, `=lib(...)` returns undefined for
    // missing lookups so a label doesn't briefly show "0" before
    // real data loads. That was right for labels but breaks
    // arithmetic like `=lib(...) * quantity` (undefined * anything
    // = NaN, and NaN poisons everything downstream). Admin wraps
    // the calculation site in num() to opt into 0-coercion for
    // exactly that expression: `=num(lib(...)) * quantity`.
    //
    // Explicit default handles the case where 0 isn't the right
    // sentinel: `=num(lib(...), 100)` for a fee that defaults to
    // 100 when unspecified.
    num: (value, defaultValue) => {
      const fallback = defaultValue === undefined ? 0 : Number(defaultValue);
      if (value === undefined || value === null || value === '') return fallback;
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : fallback;
      }
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    },

    // 0.38.28 - max(a, b, c, ...) and min(a, b, c, ...) - variadic,
    // Excel-style. Any argument may be a scalar or an array; arrays
    // are flattened one level (nested arrays don't recurse - one
    // multiselect value list is the intended shape). Non-numeric
    // items are SKIPPED silently, matching Excel's MAX/MIN behavior
    // for text and blanks. If no numeric items remain, both return
    // 0 (so `max(counta(x) - 1, 0)` cleanly clamps a possibly-
    // negative expression at zero).
    //
    // Numeric strings are accepted (library values are usually
    // stored as text). No leading-zero rejection here - admin has
    // explicitly asked for max/min, so trust the intent.
    //
    // Common uses:
    //
    //   max(counta(multiselect_1) - 1, 0)  clamp at zero
    //   max(a, b, c)                       three scalars
    //   min(user_input, max_allowed)       cap at max
    //   max(prices_list)                   max of an array
    //   max(labels_of(multiselect_ids))    max of a multiselect's
    //                                       selected value list
    //                                       (when values are numeric)
    max: (...args) => {
      const nums = _extractNumbers(args);
      return nums.length === 0 ? 0 : Math.max(...nums);
    },
    min: (...args) => {
      const nums = _extractNumbers(args);
      return nums.length === 0 ? 0 : Math.min(...nums);
    },

    // 0.48.98 - sum(a, b, list...) adds every number it is given.
    // Lists are flattened one level, numeric strings count, empty
    // values and text are skipped. Rounded to 10 places so 0.1 + 0.2
    // shows as 0.3.
    //   sum(column(table_payments_checked, "amount"))
    sum: (...args) => {
      const t = _extractNumbers(args).reduce((a, n) => a + n, 0);
      return Math.round(t * 1e10) / 1e10;
    },
    // 0.48.98 - column(list, "name") picks one value from every row
    // of a list of rows, by key or dotted path (c3, amount,
    // payment.size). Not a list gives an empty list.
    column: (list, name) => (Array.isArray(list) ? list : [])
      .map(r => (r && typeof r === 'object') ? readPath(r, String(name ?? '')) : undefined),

    // 0.38.94 - if(condition, valueIfTrue, valueIfFalse). The
    // condition is typically a comparison (a > b, x = "cash", etc.),
    // which the parser evaluates to 1 (true) or 0 (false) before
    // if() is called. Any truthy/falsy value works though:
    //   - condition truthy  -> returns valueIfTrue
    //   - condition falsy   -> returns valueIfFalse
    // "Falsy" here means: 0, '', null, undefined, false, or the
    // string "0" / "false" (admin might compare library text). Any
    // other value (including NaN? no - NaN is treated falsy) is
    // truthy.
    //
    // NOTE: both branches are evaluated before if() runs (the
    // expression engine has no short-circuit) - fine because
    // expressions are pure (no side effects). valueIfFalse is
    // optional; omitted -> '' when the condition is false.
    //
    // Examples:
    //   if(count_of_priorities > 0, "has priorities", "none")
    //   if(dropdown_owner_size = "small", 10, 0)
    //   =base * (100 - if(is_member = "yes", 20, 0)) / 100
    if: (condition, valueIfTrue, valueIfFalse) => {
      const falsy = (
        condition === 0 || condition === '' || condition === null
        || condition === undefined || condition === false
        || condition === '0' || condition === 'false'
        || (typeof condition === 'number' && Number.isNaN(condition))
      );
      if (falsy) {
        return valueIfFalse === undefined ? '' : valueIfFalse;
      }
      return valueIfTrue === undefined ? '' : valueIfTrue;
    },

    // 0.38.14 - Format a number for display using a Unicode CLDR
    // pattern string. Locale-aware, standards-based, familiar to
    // any admin who has used Excel / Java DecimalFormat / ICU /
    // Google Sheets number formats.
    //
    //   format(value)                       → plain locale-default
    //                                         formatting (no pattern)
    //   format(value, pattern)              → CLDR pattern, current
    //                                         browser locale
    //   format(value, pattern, locale)      → CLDR pattern, explicit
    //                                         BCP-47 locale
    //
    // Supported pattern characters (subset of full CLDR spec):
    //
    //     0        required digit position (always shown, even 0)
    //     #        optional digit position (shown only if present)
    //     .        decimal separator
    //     ,        grouping separator (actual character comes from
    //              locale; presence toggles grouping on)
    //     %        percent style (multiplies value by 100 in output)
    //     ;        separates positive pattern from negative pattern,
    //              e.g. "0.00;(0.00)" wraps negatives in parens
    //
    // Examples (value = 1234.5, browser locale = en-US):
    //
    //     format(1234.5, "0")            → "1235"
    //     format(1234.5, "0.00")         → "1234.50"
    //     format(1234.5, "#,##0")        → "1,235"
    //     format(1234.5, "#,##0.00")     → "1,234.50"
    //     format(1234.5, "#,##0.###")    → "1,234.5"     (up to 3, extras trimmed)
    //     format(0.15, "0.00%")          → "15.00%"      (percent style)
    //     format(-42, "0.00;(0.00)")     → "(42.00)"     (parens for negative)
    //     format(1234.5, "#,##0.00", "hy-AM") → forced Armenian style
    //
    // The pattern's `,` position is a hint that "yes, use grouping" -
    // the exact grouping RULES come from the locale (Armenian doesn't
    // group under 5 digits; Indian groups differently). This is why
    // CLDR is more powerful than passing separator characters
    // directly: one pattern renders correctly in every locale.
    //
    // Invalid input (undefined, empty string, non-numeric) returns
    // empty string.
    //
    // Composes with num():
    //     =format(num(lib(...)) * qty, "#,##0.00")
    // gives a locale-aware currency-style string, or "" if the lookup
    // fails entirely and qty is unset.
    // 0.39.81 - substitute(text, old, new, [instance]) - Excel's
    // SUBSTITUTE. Literal (non-regex) find-and-replace of `old` with
    // `new` inside `text`. Replaces ALL occurrences by default; if the
    // optional 1-based `instance` is given, only that occurrence is
    // replaced. Empty `old` returns `text` unchanged (Excel behaviour,
    // avoids an infinite/degenerate replace).
    substitute: (text, oldText, newText, instance) => {
      const s = (text === undefined || text === null) ? '' : String(text);
      const find = (oldText === undefined || oldText === null) ? '' : String(oldText);
      const repl = (newText === undefined || newText === null) ? '' : String(newText);
      if (find === '') return s;
      // Nth-instance mode.
      if (instance !== undefined && instance !== null && instance !== '') {
        const n = Number(instance);
        if (!Number.isFinite(n) || n < 1) return s;
        let idx = -1;
        let from = 0;
        for (let count = 0; count < n; count++) {
          idx = s.indexOf(find, from);
          if (idx === -1) return s; // fewer than n occurrences
          from = idx + find.length;
        }
        return s.slice(0, idx) + repl + s.slice(idx + find.length);
      }
      // Replace-all (literal). split/join avoids regex escaping.
      return s.split(find).join(repl);
    },
    // 0.39.86 - newline() / br() produce line breaks for use inside
    // expressions, since the expression string tokenizer does NOT
    // interpret escape sequences (typing "\n" gives a literal
    // backslash-n). Use them with substitute to turn a delimited
    // string into separate lines:
    //   =substitute(some_text, ";", br())
    // In the markdown renderer a blank line (\n\n) starts a new
    // paragraph, so br() (a double newline) reliably renders each
    // fragment on its own line. newline() is a single \n for callers
    // that want a soft break.
    newline: () => '\n',
    br: () => '\n\n',
    // 0.44.60 - removemd(text): the text without its Markdown signs
    // (** __ * _ ~~ ` # > list markers, links and images become their
    // text). For Markdown from a library, an API or a form field shown in
    // a plain field: =removemd(lib(...)), =removemd(api(...)). A list
    // is cleaned item by item. Collection Rich text columns need no
    // wrapping: <key> is already plain, <key>_md the Markdown.
    removemd: (text) => (Array.isArray(text)
      ? text.map(t => stripMarkdown(t))
      : stripMarkdown(text)),
    // 0.44.70 - formatdate(value, pattern): an ISO date (2026-10-03, or
    // 2026-10-03T14:30) in a pattern: formatdate(d, "dd MMMM yyyy").
    // No pattern: the platform's date format. parsedate(text, pattern):
    // the reverse, giving ISO, for a date inside a text. Both give ''
    // for what is not a date; a list is converted item by item.
    /* 0.49.07 - Dates in formulas, all as ISO text so formatdate and a
       Date column show them the platform's way:
         today()            2026-10-30, in the person's time zone (else
                            the platform's)
         now()              2026-10-30T16:30
         adddays(date, n)   the date n days later, earlier when n is
                            negative; a time on the date is kept
         days(from, to)     whole calendar days from one date to the
                            other, negative when `to` is earlier
       A date that is not ISO (written 30.10.2026) gives '': read it
       with parsedate first. */
    today: () => _wallNow().slice(0, 10),
    now: () => _wallNow(),
    adddays: (date, n) => {
      const v = readIso(date);
      const k = Number(n);
      if (!v || !Number.isFinite(k)) return '';
      const d = new Date(Date.UTC(v.y, v.m - 1, v.d + Math.trunc(k)));
      const day = d.toISOString().slice(0, 10);
      return v.time ? `${day}T${String(v.h).padStart(2, '0')}:${String(v.mi).padStart(2, '0')}` : day;
    },
    days: (from, to) => {
      const a = readIso(from);
      const b = readIso(to);
      if (!a || !b) return '';
      return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000);
    },
    formatdate: (value, pattern) => (Array.isArray(value)
      ? value.map(v => formatDatePattern(v, pattern))
      : formatDatePattern(value, pattern)),
    parsedate: (text, pattern) => (Array.isArray(text)
      ? text.map(t => parseDatePattern(t, pattern))
      : parseDatePattern(text, pattern)),
    format: (value, pattern, locale, groupFrom) => {
      if (value === undefined || value === null || value === '') return '';
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return '';

      /* 0.42.21 - Group only from a given number of digits.
       *
       * A four-digit year, a room number or a form number reads worse
       * with a separator: 2026 is a year, "2,026" looks like a
       * quantity. The threshold says how many integer digits a number
       * must have before it is grouped at all.
       *
       * 5 means 1234 stays 1234 and 12345 becomes 12,345. Applied by
       * turning grouping OFF for the short ones, so the pattern and
       * locale still decide everything else. */
      /* 0.42.47 - Precedence for both the locale and the grouping
       * threshold: the ARGUMENT, then the PLATFORM setting (Settings ->
       * Number formatting), then the viewer's browser.
       *
       * Before, an explicit format() skipped the platform entirely: it
       * ignored "Group digits from" unless given a fourth argument, and
       * without a locale followed the BROWSER - so one page showed
       * 1,234.5 in one browser and 1 234,5 in another. Now it formats
       * exactly as the numbers the platform formats on its own.
       *
       * An explicit 0 for the threshold still means "always group". */
      const platformNf = getNumberFormatDefaults();
      const given = (x) => x !== undefined && x !== null && x !== '';
      locale = given(locale) ? locale : (platformNf.defaultLocale || '');
      const threshold = given(groupFrom)
        ? (Number(groupFrom) || 0)
        : (Number(platformNf.groupFromDigits) || 0);
      if (threshold > 0) {
        const digits = Math.floor(Math.abs(n)).toString().length;
        if (digits < threshold) {
          const opts = { useGrouping: false };
          const req = (locale !== undefined && locale !== null && locale !== '')
            ? String(locale)
            : (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
          // The pattern's decimal places still apply; only the
          // separator is dropped.
          if (pattern && typeof pattern === 'string') {
            const dot = pattern.replace(/[^0#.]/g, '').indexOf('.');
            const frac = dot === -1 ? '' : pattern.replace(/[^0#.]/g, '').slice(dot + 1);
            opts.minimumFractionDigits = (frac.match(/0/g) || []).length;
            opts.maximumFractionDigits = opts.minimumFractionDigits
              + (frac.match(/#/g) || []).length;
          }
          try {
            return new Intl.NumberFormat(req, opts).format(n);
          } catch {
            return String(n);
          }
        }
      }

      // Locale precedence (0.42.47):
      //   1. explicit `locale` argument if provided
      //   2. the platform's default locale (already folded into
      //      `locale` at the top of this function)
      //   3. the citizen's browser locale, only when neither is set
      //   4. "en-US" fallback if Intl rejects the chosen locale
      const requestedLocale = (locale !== undefined && locale !== null && locale !== '')
        ? String(locale)
        : (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

      let options = {};
      let negWrap = null;
      if (pattern !== undefined && pattern !== null && pattern !== '') {
        if (typeof pattern !== 'string') {
          // 0.38.14 - Only string patterns are supported. The
          // pre-0.38.14 signature `format(x, decimals)` where
          // decimals was a Number is intentionally no longer
          // accepted; admin should migrate to the pattern form.
          // Log to console for debuggability but degrade gracefully.
          if (typeof console !== 'undefined' && console.warn) {
            console.warn(
              '[format] Second argument must be a CLDR pattern string '
              + '(e.g. "#,##0.00"). Number arguments no longer supported '
              + 'since 0.38.14. Got:', typeof pattern
            );
          }
          return '';
        }
        const parsed = _parseCldrPattern(pattern);
        options = parsed.options;
        negWrap = parsed.negWrap;
      }

      // Negative wrap (e.g. parens): format the absolute value and
      // apply the pattern's negative prefix/suffix manually. Intl's
      // signDisplay doesn't cover paren-wrapping directly.
      const useNegWrap = negWrap && n < 0;
      const valueForFormat = useNegWrap ? Math.abs(n) : n;

      let formatted;
      try {
        formatted = new Intl.NumberFormat(requestedLocale, options).format(valueForFormat);
      } catch (_e) {
        try {
          formatted = new Intl.NumberFormat('en-US', options).format(valueForFormat);
        } catch (_e2) {
          // Absolute last resort - Intl unavailable or broken.
          // Return a plain string that at least isn't "NaN".
          if (options.minimumFractionDigits !== undefined) {
            formatted = valueForFormat.toFixed(options.minimumFractionDigits);
          } else {
            formatted = String(valueForFormat);
          }
        }
      }

      if (useNegWrap) {
        return negWrap.prefix + formatted + negWrap.suffix;
      }
      return formatted;
    },

    // 0.38.21 - Opt-out functions for the auto-format-numeric-string
    // behavior. resolveAdminText will (as of this ship) coerce a
    // string that looks like a bare number into a Number and pass
    // it through the format() cascade - so `=lib(..., "state_duty_rate")`
    // returning "30000" gets displayed as "30,000.00" automatically.
    // That's great for money-like values in library columns but
    // wrong for years (2024 -> "2,024") and for numeric-looking
    // strings that are actually identifiers (postal codes,
    // reference numbers, etc.).
    //
    // Both functions append EXPLICIT_STRING_MARKER (U+2060 Word
    // Joiner, invisible in every renderer) to their return value.
    // The auto-detect regex requires all-digits with no other
    // characters, so a marked string never matches and stays raw.
    // resolveAdminText strips the marker before final display so
    // citizens see clean output.
    //
    //   year(x)  - integer-string form of x. Floors non-integer
    //              input; returns "" if x is not a number. Common
    //              use: `=year(2024)` in a hint text renders "2024"
    //              regardless of the platform's #,##0 default.
    //   str(x)   - any-value-to-string. Passes lib results, field
    //              references, or literals through String() and
    //              marks them as "don't auto-format". Use when a
    //              numeric-looking library column (phone number,
    //              postal code, ID) should not pick up currency
    //              formatting from the cascade.
    //
    // Interop caveat: num(str(x)) and format(str(x), "...") return
    // 0 / "" respectively, because those functions call Number() on
    // the marked string which yields NaN. That is a correct
    // "contradictory intent" outcome; admin should not chain str()
    // through numeric functions.
    year: (value) => {
      if (value === undefined || value === null || value === '') return '';
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return '';
      return String(Math.floor(n)) + EXPLICIT_STRING_MARKER;
    },
    str: (value) => {
      if (value === undefined || value === null) return '';
      return String(value) + EXPLICIT_STRING_MARKER;
    },

    // 0.46.29 - lower(text) / upper(text): the text in small or capital
    // letters, as in Excel. For matching a value to a library whose
    // keys are written another way: the Countries and dial codes
    // library keeps Alpha-2 codes in small letters ("be"), an API may
    // send "BE", and lib() matches exactly. A str() marker survives.
    lower: (value) => {
      if (value === undefined || value === null) return '';
      return String(value).toLowerCase();
    },
    upper: (value) => {
      if (value === undefined || value === null) return '';
      // 0.48.22 - Armenian "և" as "ԵՎ", not Unicode's "ԵՒ".
      return upperHy(value);
    },

    // 0.38.24 - concat() and the `&` operator both call
    // functions.concat(...args) to stitch values into a single
    // string. Each argument runs through the same cascade the
    // renderer uses for `=expression` results and `{field}`
    // interpolation, so `concat(total, " AMD")` renders as
    // "120,000 AMD" per platform default without an explicit
    // format() wrapper. Opt-out markers from year()/str() are
    // honored (fragment stays raw) and numeric-looking strings
    // are auto-formatted the same way they are in text-display
    // contexts.
    //
    // Precedence when used via `&`: lowest (below +/-/*//), so
    // `a + b & " AMD"` parses as `(a + b) & " AMD"`. Result of
    // `&` is always a String; downstream `+`, `-`, `*`, `/` will
    // coerce as usual if further arithmetic is chained.
    //
    // Note about interop with num()/format(): once concatenated,
    // the result is a String and no longer safe to num() back
    // (contains grouping separators, unit suffixes, etc). Compose
    // like Excel: keep numeric values raw for math, concat only
    // at the last step for display.
    concat: (...args) => {
      const fragment = (v) => {
        if (v == null) return '';
        // Opt-out: strip marker, return raw.
        if (typeof v === 'string' && v.indexOf(EXPLICIT_STRING_MARKER) !== -1) {
          return v.split(EXPLICIT_STRING_MARKER).join('');
        }
        // Number detection - same rules as resolveAdminText.
        let n = null;
        if (typeof v === 'number' && Number.isFinite(v)) {
          n = v;
        } else if (typeof v === 'string') {
          const t = v.trim();
          if (/^-?\d+(\.\d+)?$/.test(t) && !/^-?0\d/.test(t)) {
            const p = Number(t);
            if (Number.isFinite(p)) n = p;
          }
        }
        if (n !== null) {
          const perForm = formNumberDefaults || {};
          const perPlatform = getNumberFormatDefaults();
          const pattern = perForm.default_format || perPlatform.defaultFormat || '';
          const locale = perForm.default_locale || perPlatform.defaultLocale || '';
          if (pattern) {
            // Reuse the exported format function so CLDR handling
            // stays in one place. If format() isn't defined yet
            // (edge case - shouldn't happen since it's in the
            // same object below), fall back to String().
            try {
              const formatted = new Intl.NumberFormat(
                // 0.42.47 - platform default before the browser, as format().
                locale || getNumberFormatDefaults().defaultLocale
                  || (typeof navigator !== 'undefined' && navigator.language) || 'en-US',
                _parseCldrPattern(pattern).options,
              ).format(n);
              return formatted;
            } catch (_e) {
              return String(v);
            }
          }
        }
        return String(v);
      };
      return args.map(fragment).join('');
    },
  };
}

// 0.38.14 - CLDR pattern parser. Translates a Unicode CLDR-style
// number pattern into Intl.NumberFormat options plus any negative
// prefix/suffix that needs manual wrapping (Intl's signDisplay
// doesn't cover paren-negatives like "0.00;(0.00)").
//
// Supports the subset admin actually uses in gov forms:
//     0  required digit    #  optional digit    ,  grouping
//     .  decimal           %  percent           ;  neg subpattern
//
// Not implemented (may be added later if admin asks):
//     ¤  currency symbol
//     E  scientific notation
//     ‰  per-mille
//     '..'  quoted literals inside the pattern
//
// The parser is lenient: `#,##.##` (missing the strict-CLDR `0`
// before the decimal) is treated as `#,##0.##` rather than
// rejected. Full spec would require at least one `0`, but demanding
// that in an admin-facing tool is a needless papercut.
function _parseCldrPattern(pattern) {
  const semi = pattern.indexOf(';');
  const positivePart = semi >= 0 ? pattern.slice(0, semi) : pattern;
  const negativePart = semi >= 0 ? pattern.slice(semi + 1) : null;

  const isPercent = positivePart.endsWith('%');
  const posBody = isPercent ? positivePart.slice(0, -1) : positivePart;

  const dot = posBody.indexOf('.');
  const intPart = dot >= 0 ? posBody.slice(0, dot) : posBody;
  const fracPart = dot >= 0 ? posBody.slice(dot + 1) : '';

  // Count required (0) and optional (#) digits.
  const minIntDigits = Math.max(1, (intPart.match(/0/g) || []).length);
  const minFractionDigits = (fracPart.match(/0/g) || []).length;
  const maxFractionDigits = minFractionDigits + (fracPart.match(/#/g) || []).length;
  const useGrouping = intPart.includes(',');

  const options = {
    minimumIntegerDigits: minIntDigits,
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: Math.max(minFractionDigits, maxFractionDigits),
    useGrouping,
  };
  if (isPercent) options.style = 'percent';

  // Negative subpattern wrap. Extract the literal characters that
  // frame the digit portion, e.g. "(0.00)" -> prefix "(", suffix ")".
  // If negative subpattern is missing or matches positive exactly,
  // no wrap is needed (Intl's default sign display handles it).
  let negWrap = null;
  if (negativePart) {
    const negBody = negativePart.endsWith('%') ? negativePart.slice(0, -1) : negativePart;
    const digitPattern = /[0#,.]+/;
    const m = negBody.match(digitPattern);
    if (m) {
      const prefix = negBody.slice(0, m.index);
      const suffix = negBody.slice(m.index + m[0].length);
      if (prefix || suffix) {
        negWrap = { prefix, suffix };
      }
    }
  }

  return { options, negWrap };
}

// Small structure walker used by labels_of. Same shape as
// findElementByName in PageContentRenderer but scoped to
// evaluate.js so the module doesn't import the renderer.
function _findElByEid(structure, eid) {
  if (!Array.isArray(structure)) return null;
  for (const el of structure) {
    if (!el || typeof el !== 'object') continue;
    const thisEid = el.element_id || el.name || '';
    if (thisEid === eid) return el;
    if (Array.isArray(el.children)) {
      const found = _findElByEid(el.children, eid);
      if (found) return found;
    }
  }
  return null;
}

// 0.38.28 - Extract numeric values from a mixed list of scalars and
// arrays for max() / min(). Arrays are flattened one level; nested
// arrays don't recurse (multiselect values are a flat list, one
// level is all that's needed). Non-numeric items - undefined, null,
// non-numeric strings, booleans, objects - are silently skipped.
// Numeric strings ARE coerced, no leading-zero rejection: admin
// explicitly called max/min so intent is clear.
/* 0.49.07 - The wall-clock date and time now, as 2026-10-30T16:30, in
   the time zone dates are shown in. */
function _wallNow() {
  const parts = {};
  try {
    for (const p of new Intl.DateTimeFormat('en-GB', {
      timeZone: currentTimezone(), year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date())) parts[p.type] = p.value;
  } catch (_e) {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function _extractNumbers(args) {
  const nums = [];
  const push = (item) => {
    if (typeof item === 'number' && Number.isFinite(item)) {
      nums.push(item);
    } else if (typeof item === 'string' && item !== '') {
      // 0.49.01 - Written the way people write numbers too: "12,000",
      // "12 000", "1,500.75 AMD", what format() gives back.
      const n = looseNumber(item);
      if (n !== null) nums.push(n);
    }
    // undefined, null, empty string, unparseable string, bool,
    // object: skip silently (matches Excel's MAX/MIN treatment
    // of non-numeric cells).
  };
  for (const arg of args) {
    if (Array.isArray(arg)) {
      for (const item of arg) push(item);
    } else {
      push(arg);
    }
  }
  return nums;
}
